import * as React from 'react';
import { ApolloConsumer } from 'react-apollo';

import type ApolloClient from 'apollo-client';

import type { PatchedLocationDescriptor } from 'js/lib/connectToRouter';
import { simplifyQueryValue } from 'js/lib/queryUtils';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import PlagiarismProvider from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import type { Item } from 'bundles/author-common/types/Item';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { ProctorConfig } from 'bundles/authoring/common/types/proctorConfig';
import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common/utils/epicUtils';
import GradingMutationWrapper from 'bundles/grading/api/GradingMutationWrapper';
import submissionDataQuery from 'bundles/grading/api/SubmissionDataQuery.graphql';
import submissionDataQueryV2 from 'bundles/grading/api/SubmissionDataQueryV2.graphql';
import { ItemType } from 'bundles/grading/api/types';
import ResetCacheOnUnmountWrapper from 'bundles/grading/components/common/ResetCacheOnUnmountWrapper';
import type {
  GetUrlFn,
  SGAPageContextType,
  SgaPageData,
} from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import AiSuggestionsProvider from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsProvider';
import { AutoSaveProvider } from 'bundles/grading/components/staff-graded-assignments/auto-save/AutoSaveProvider';
import InlineFeedbackRubricProvider from 'bundles/grading/components/staff-graded-assignments/form-parts/InlineFeedbackRubricProvider';
import type { SGASubmitter, Team } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import useAssignmentInfoQuery from 'bundles/grading/hooks/useAssignmentInfoQuery';
import useSubmissionDataQuery from 'bundles/grading/hooks/useSubmissionDataQuery';
import useSubmissionHeadersQuery from 'bundles/grading/hooks/useSubmissionHeadersQuery';
import useSubmitterListQuery from 'bundles/grading/hooks/useSubmitterListQuery';
import { ReentryLogManager } from 'bundles/grading/utils/ReentryLogManager';
import {
  generateSubmissionDataQueryVariables,
  generateSubmitterListQueryVariables,
} from 'bundles/grading/utils/apiUtils';
import {
  getNewSubmitterIdFromLegacyId,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';

const useGetUrl = (): GetUrlFn => {
  const routeLocation = useLocation();
  const routeParams = useParams();
  const routes = useRoutes();
  const { contextId, itemId, id: submitterId } = routeParams;

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const routeName = routes[routes.length - 1].name;

  const gradingGroupId = routeLocation.query?.groupId;
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const reentryLog = new ReentryLogManager(contextId, itemId, simplifyQueryValue(gradingGroupId));

  return (learnerId: string, partId = '', rubricId = '', lastPart = false): PatchedLocationDescriptor => {
    const updatedParams = {
      userId: learnerId,
      id: learnerId,
    };

    const queryString: { partId?: string; rubricId?: string; groupId?: string } = {};
    if (partId) {
      queryString.partId = partId;
    }
    if (rubricId) {
      queryString.rubricId = rubricId;
    }

    const lastPartQueryString: { 'last-part': boolean; groupId?: string } = {
      'last-part': true,
    };

    if (gradingGroupId) {
      queryString.groupId = simplifyQueryValue(gradingGroupId);
      lastPartQueryString.groupId = simplifyQueryValue(gradingGroupId);
    }

    // save current session to local storage
    reentryLog.saveCurrentSessionToLocalStorage({
      // using the router to update query params to avoid rerendering of components to not overwrite ids with undefined
      submitterId,
      partId: simplifyQueryValue(routeLocation.query?.partId),
      rubricId: simplifyQueryValue(routeLocation.query?.rubricId),
      lastPart: simplifyQueryValue(routeLocation.query?.['last-part']),
    });

    return {
      name: routeName,
      query: lastPart ? lastPartQueryString : queryString,
      params: Object.assign({}, routeParams, updatedParams),
    };
  };
};

/**
 * This function will call the Apollo client to query submission for the next or previous submitter.
 * The Apollo client will store the response in the Apollo cache so when the grader navigates
 * to the next or previous submitter, the data are loaded immediately from the cache.
 *
 * @param submitter the next or previous submitter
 * @param pageData the page data
 * @param apolloClient the Apollo client
 */
const preFetchSubmission = (submitter: SGASubmitter, pageData: SgaPageData, apolloClient: ApolloClient<$TSFixMe>) => {
  const submitterId = getNewSubmitterIdFromLegacyId(submitter.id);

  // The result of the query can be ignored here, the Apollo client will store the response in the cache.
  if (isAssignmentGradingInPublicEnabled()) {
    const queryVariables = {
      itemId: pageData.currentItemId,
      courseId: pageData.courseId,
      scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(submitter.id, pageData.gradingGroupId),
    };
    apolloClient.query({
      query: submissionDataQueryV2,
      variables: queryVariables,
      context: { clientName: 'gatewayGql' },
    });
  } else {
    const queryVariables = generateSubmissionDataQueryVariables(
      pageData.currentContextId,
      pageData.currentItemId,
      ItemType.StaffGradedAssignment,
      submitterId,
      pageData.gradingGroupId
    );
    apolloClient.query({
      query: submissionDataQuery,
      variables: queryVariables,
      context: { clientName: 'gatewayGql' },
    });
  }
};

type Props = {
  children: React.ReactNode;
  courseId: string;
  currentItem: Item;
  courseBranchId: string;
  sessionItem: SessionItem;
  gradebookGrades: { [userId: string]: ItemGrade };
  isAnonymized: boolean;
  proctorConfigDraft: ProctorConfig;
  // for private sessions only
  groupId?: string;
};
/**
 * SGAPageContextWrapper fetches submittersList and the latest submission for a current learner.
 * It stores fetched data as a single source of truth in React Context object to be accessed by the page child components.
 */
export const SGAPageContextProvider = ({
  children,
  courseId,
  currentItem,
  courseBranchId,
  sessionItem,
  gradebookGrades,
  isAnonymized,
  proctorConfigDraft,
  groupId,
}: Props) => {
  const routeParams = useParams();
  const routeLocation = useLocation();

  const isPrivateSession = !!groupId;

  // retrieving base assignment ids for items query
  const pageData: SgaPageData = {
    courseId,
    courseBranchId,
    currentItem,
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    currentItemId: routeParams.itemId,
    groupId,
    gradingGroupId: simplifyQueryValue(routeLocation.query?.groupId),
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    currentCourseSlug: routeParams.courseSlug,
    // evaluates to groupId in Private session and authoringBranchId in Public
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    currentContextId: routeParams.contextId,
    sessionItem,
    gradebookGrades,
    isAnonymized,
    proctorConfigDraft,
  };

  // grab reentry log ids if they exist and apply it to current submitter before running any queries
  // this will help fetching submission data for a submitter (if any in log) while the submitter list is being loaded
  // but before doing so, delete any stale log items before creating a new instance
  ReentryLogManager.removeStaleReentryLogItems();
  const reentryLog = new ReentryLogManager(pageData.currentContextId, pageData.currentItemId, pageData.gradingGroupId);

  const currentSubmitterId = routeParams.id || routeParams.userId;
  const submitterId = currentSubmitterId ? getNewSubmitterIdFromLegacyId(currentSubmitterId) : '';

  const submissionDataQueryVariables = generateSubmissionDataQueryVariables(
    pageData.currentContextId,
    pageData.currentItemId,
    ItemType.StaffGradedAssignment,
    submitterId,
    pageData.gradingGroupId
  );

  const getUrl = useGetUrl();

  const submissionHeaders = useSubmissionHeadersQuery(
    submissionDataQueryVariables,
    pageData.courseId,
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    currentSubmitterId,
    pageData.gradingGroupId
  );
  const submitterList = useSubmitterListQuery(
    generateSubmitterListQueryVariables(
      pageData.currentContextId,
      pageData.currentItemId,
      ItemType.StaffGradedAssignment,
      pageData.gradingGroupId
    ),
    !isPrivateSession
  );

  const assignmentInfo = useAssignmentInfoQuery(
    generateSubmitterListQueryVariables(
      pageData.currentContextId,
      pageData.currentItemId,
      ItemType.StaffGradedAssignment,
      pageData.gradingGroupId
    ),
    !isPrivateSession
  );

  const submissionData = useSubmissionDataQuery(
    submissionDataQueryVariables,
    pageData.courseId,
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    currentSubmitterId,
    pageData.groupId,
    pageData.gradingGroupId
  );
  return (
    // GradingMutationWrapper must be the outer wrapper to ensure any mutation actions that require cache
    // updating, triggers appropriate processing for all nested queries
    <GradingMutationWrapper courseId={pageData.courseId}>
      {(mutations) => {
        const contextValue: SGAPageContextType = {
          isPrivateSession,
          submissionData,
          submissionHeaders,
          submitterList,
          assignmentInfo,
          pageData,
          getUrl,
          reentryLog,
          mutations,
        };

        const lastExecutionId = submissionHeaders.data && submissionHeaders.data[0] && submissionHeaders.data[0].id;

        if (!isPrivateSession) {
          return (
            <SGAPageContext.Provider value={contextValue}>
              <AiSuggestionsProvider>
                <AutoSaveProvider mutations={mutations}>
                  <InlineFeedbackRubricProvider>
                    <ApolloConsumer>
                      {(apolloClient) => {
                        return (
                          // TODO: Deprecate cache resetting once quick grader is default experience
                          <ResetCacheOnUnmountWrapper apolloClient={apolloClient}>
                            {children}
                          </ResetCacheOnUnmountWrapper>
                        );
                      }}
                    </ApolloConsumer>
                  </InlineFeedbackRubricProvider>
                </AutoSaveProvider>
              </AiSuggestionsProvider>
            </SGAPageContext.Provider>
          );
        }

        return (
          <SGAPageContext.Provider value={contextValue}>
            <AiSuggestionsProvider>
              <AutoSaveProvider mutations={mutations}>
                <InlineFeedbackRubricProvider>
                  <ApolloConsumer>
                    {(apolloClient) => {
                      // Pre-fetch next and previous submission
                      if (submitterList.data?.nextSubmitter) {
                        preFetchSubmission(submitterList.data.nextSubmitter, pageData, apolloClient);
                      }
                      if (submitterList.data?.previousSubmitter) {
                        preFetchSubmission(submitterList.data.previousSubmitter, pageData, apolloClient);
                      }

                      return (
                        // TODO: Deprecate cache resetting once quick grader is default experience
                        <ResetCacheOnUnmountWrapper apolloClient={apolloClient}>
                          {currentSubmitterId && (
                            <PlagiarismProvider
                              propsLoading={submissionData.loading || !submissionData.data}
                              courseId={pageData.courseId}
                              itemId={pageData.currentItem.id}
                              userId={currentSubmitterId}
                              groupId={pageData.currentContextId}
                              courseBranchId={pageData.courseBranchId}
                              submissionKey={
                                lastExecutionId && tupleToStringKey(['workflowSubmission', lastExecutionId])
                              }
                              team={
                                submissionData.data?.isTeamAssignment && submissionData.data.submitter
                                  ? {
                                      name: submissionData.data.submitter.name,
                                      id: submissionData.data.submitter.id,
                                      members: (submissionData.data.submitter as Team).userProfiles.map(({ userId }) =>
                                        Number(userId)
                                      ),
                                    }
                                  : undefined
                              }
                              isAnonymized={pageData.isAnonymized}
                              assignmentId={(currentItem?.definition as Record<string, string>)?.assignmentId}
                              sessionId={sessionItem.sessionId}
                            >
                              {children}
                            </PlagiarismProvider>
                          )}
                        </ResetCacheOnUnmountWrapper>
                      );
                    }}
                  </ApolloConsumer>
                </InlineFeedbackRubricProvider>
              </AutoSaveProvider>
            </AiSuggestionsProvider>
          </SGAPageContext.Provider>
        );
      }}
    </GradingMutationWrapper>
  );
};
